/**
 * Shared rotation transform logic for SolidMesh transform panels.
 *
 * Extracted from SolidMeshTransformPanelManual.vue and
 * SolidMeshTransformPanelSinglePoint.vue to eliminate ~90% code duplication.
 */

/**
 * Apply a rotation update to a solid mesh, guarding against concurrent updates.
 * Returns `true` if the update was applied.
 */
export function applyRotationUpdate(
	solidMesh:
		| { setRotation: (r: { x: number; y: number; z: number }) => void }
		| undefined,
	rotation: { x: number; y: number; z: number },
	isUpdating: boolean,
): boolean {
	if (isUpdating || !solidMesh) return false;
	solidMesh.setRotation(rotation);
	return true;
}

/**
 * Round rotation degrees to 1 decimal place.
 */
export function roundRotation(
	rot: Readonly<{ x?: number; y?: number; z?: number }>,
): { x: number; y: number; z: number } {
	return {
		x: Math.round((rot.x ?? 0) * 10) / 10,
		y: Math.round((rot.y ?? 0) * 10) / 10,
		z: Math.round((rot.z ?? 0) * 10) / 10,
	};
}

/**
 * Normalize a rotation value into [-180, 180] range, rounded to 1 decimal.
 */
export function normalizeRotation(v: number): number {
	let r = (((v % 360) + 540) % 360) - 180;
	if (r === -180) r = 180;
	return Math.round(r * 10) / 10;
}
